import { memo, PointerEvent as ReactPointerEvent } from 'react';

import { CanvasElement, Point } from '@/features/canvas/types';
import { connectorGeometry } from './geometry';

/** An in-progress gesture preview drawn in world space (rendered by DraftLayer). */
export type Draft =
  | { kind: 'pen'; stroke: number[][]; color: string; width: number; highlighter?: boolean }
  | {
      kind: 'connector';
      from: { x: number; y: number; w: number; h: number };
      to: Point; // the free cursor point (used when not hovering a target)
      toBox?: { x: number; y: number; w: number; h: number }; // the hovered target's box, if any
      fromAnchor?: { nx: number; ny: number }; // pinned start edge (nub / border drag)
      toAnchor?: { nx: number; ny: number }; // pinned end edge (cursor's spot on the target)
      color: string;
    }
  | { kind: 'create'; rect: { x: number; y: number; w: number; h: number } }
  | null;

interface Props {
  elements: CanvasElement[];
  byId: (id: string) => CanvasElement | undefined;
  onElementPointerDown: (e: ReactPointerEvent, id: string) => void;
  onConnectorDoubleClick: (id: string) => void;
}

/**
 * The SVG layer, now just a transparent HIT layer for connectors (the visible connector lines + the
 * freehand ink render on the GPU / PixiInkLayer). Kept in world space so the fat hit paths track the
 * connectors; memoized so a camera pan/zoom doesn't re-run it.
 */
function OverlaySvg({ elements, byId, onElementPointerDown, onConnectorDoubleClick }: Props) {
  return (
    <svg
      style={{ position: 'absolute', left: 0, top: 0, width: 1, height: 1, overflow: 'visible', pointerEvents: 'none' }}
    >
      {elements.map((el) => {
        if (el.kind === 'connector') {
          const from = el.from_id ? byId(el.from_id) : undefined;
          const to = el.to_id ? byId(el.to_id) : undefined;
          if (!from || !to) return null;
          const g = connectorGeometry(from, to, el.style ?? {});
          const width = el.style?.strokeWidth ?? 2;
          // The VISIBLE connector renders on the GPU (PixiInkLayer). This is just a fat transparent hit
          // path so the thin line stays easy to click / double-click (interactions unchanged).
          return (
            <g
              key={el.id}
              style={{ pointerEvents: 'stroke', cursor: 'pointer', touchAction: 'none' }}
              onPointerDown={(e) => onElementPointerDown(e, el.id)}
              onDoubleClick={() => onConnectorDoubleClick(el.id)}
            >
              <path d={g.d} fill="none" stroke="transparent" strokeWidth={Math.max(14, width + 12)} strokeLinecap="round" strokeLinejoin="round" />
            </g>
          );
        }
        // Freehand ink now renders on the GPU (PixiInkLayer), not here.
        return null;
      })}
    </svg>
  );
}

export default memo(OverlaySvg);
